import get from 'lodash.get';
import orderBy from 'lodash.orderby';
import range from 'lodash.range';

import {
    TRC_INTERPRETATIONS,
    D8_INTERPRETATIONS,
    IDEL2_INTERPRETATIONS,
} from '../../constants/interpretations';
import { READING, ESPANOL } from '../../constants/applications';

export const SORTING_ORDER = {
    ASC: 'asc',
    DESC: 'desc',
};

export const COLUMNS_NAMES = {
    DIBELS: 'DIBELS',
    TRC: 'TRC',
    INTERVENTION: 'mCLASS Intervention',
    NAME: 'Name',
    IDEL2: 'Spanish R&D', // TODO: Rename this parameter to 'Lectura' after CompositeScore for Lectura will be officially released
    IDEL: 'IDEL',
    INVENTORY: 'Inventory',
};

// order of application in status header - only apps with statuses
export const ORDERED_HEADER_COLUMNS = {
    [READING]: [
        COLUMNS_NAMES.DIBELS,
        COLUMNS_NAMES.TRC,
        COLUMNS_NAMES.INTERVENTION,
        COLUMNS_NAMES.NAME,
    ],
    [ESPANOL]: [COLUMNS_NAMES.IDEL2, COLUMNS_NAMES.TRC, COLUMNS_NAMES.NAME],
};

export const SECTION_NAMES = {
    INTERVENTION: 'Intervention',
    IDEL_SCREENER: 'IDEL Screener',
    IDEL: 'IDEL',
};

const READING_LEVEL_SORTING_MAP = Object.assign(
    { '<PC': 2, '&lt;PC': 2, PC: 3, RB: 4, '>Z': 199, '&gt;Z': 199 },
    ...range('A'.charCodeAt(0), 'Z'.charCodeAt(0) + 1).map(code => ({
        [String.fromCharCode(code)]: code,
    })),
);

const TRC_INTERP_SORTING_MAP = {
    [TRC_INTERPRETATIONS.UNKNOWN]: 0,
    [TRC_INTERPRETATIONS.INCOMPLETE]: 1,
    [TRC_INTERPRETATIONS.FAR_BELOW_PROFICIENT]: 2,
    [TRC_INTERPRETATIONS.BELOW_PROFICIENT]: 3,
    [TRC_INTERPRETATIONS.PROFICIENT]: 4,
    [TRC_INTERPRETATIONS.ABOVE_PROFICIENT]: 5,
};

const D8_INTERP_SORTING_MAP = {
    [D8_INTERPRETATIONS.UNKNOWN]: 0,
    [D8_INTERPRETATIONS.PAUSED]: 1,
    [D8_INTERPRETATIONS.WELL_BELOW_BENCHMARK]: 2,
    [D8_INTERPRETATIONS.BELOW_BENCHMARK]: 3,
    [D8_INTERPRETATIONS.AT_BENCHMARK]: 4,
    [D8_INTERPRETATIONS.ABOVE_BENCHMARK]: 5,
};

const IDEL2_INTERP_SORTING_MAP = {
    [IDEL2_INTERPRETATIONS.UNKNOWN]: 0,
    [IDEL2_INTERPRETATIONS.PAUSED]: 1,
    [IDEL2_INTERPRETATIONS.WELL_BELOW_BENCHMARK]: 2,
    [IDEL2_INTERPRETATIONS.BELOW_BENCHMARK]: 3,
    [IDEL2_INTERPRETATIONS.AT_BENCHMARK]: 4,
    [IDEL2_INTERPRETATIONS.ABOVE_BENCHMARK]: 5,
};

/**
 * Returns sorting rules by table column name (E.g. for TRC we will get
 * two functions for sorting students: firstly by interp and then by finalLevel)
 * Sorting rule for d8 data:
 *  - returns overallScore if such value exists or -1 if there is no data for current period.
 *  - if overallScore equals to null, it gets replaced by 0.
 * @param {string} period - time of year
 */
const getSortingRules = period => ({
    [READING]: {
        [COLUMNS_NAMES.DIBELS]: [
            student =>
                D8_INTERP_SORTING_MAP[
                    get(
                        student,
                        `appStatuses.dibels8.${period}.overallInterp`,
                        D8_INTERPRETATIONS.UNKNOWN,
                    )
                ],
            student => get(student, `appStatuses.dibels8.${period}.overallScore`, -1) || 0,
        ],
        [COLUMNS_NAMES.TRC]: [
            student => TRC_INTERP_SORTING_MAP[get(student, `appStatuses.r3d.${period}.interp`)],
            student =>
                READING_LEVEL_SORTING_MAP[get(student, `appStatuses.r3d.${period}.finalLevel`)],
        ],
        [COLUMNS_NAMES.INTERVENTION]: [
            student => get(student, `appStatuses.burst.${period}.isPlacementTestComplete`),
        ],
        [COLUMNS_NAMES.NAME]: [
            student => student.firstName.toLowerCase(),
            student => student.lastName.toLowerCase(),
        ],
    },
    [ESPANOL]: {
        [COLUMNS_NAMES.IDEL2]: [
            student =>
                IDEL2_INTERP_SORTING_MAP[
                    get(
                        student,
                        `appStatuses.idel2.${period}.overallInterp`,
                        IDEL2_INTERPRETATIONS.UNKNOWN,
                    )
                ],
            student => get(student, `appStatuses.idel2.${period}.overallScore`, -1) || 0,
        ],
        [COLUMNS_NAMES.TRC]: [
            student =>
                TRC_INTERP_SORTING_MAP[get(student, `appStatuses.r3dspanish.${period}.interp`)],
            student =>
                READING_LEVEL_SORTING_MAP[
                    get(student, `appStatuses.r3dspanish.${period}.finalLevel`)
                ],
        ],
        [COLUMNS_NAMES.NAME]: [
            student => student.firstName.toLowerCase(),
            student => student.lastName.toLowerCase(),
        ],
    },
});

/**
 * Returns array of students sorted by provided params
 * @param {array} students - array of students
 * @param {{ column: string, order: string }[]} sortingParams - array of params for sorting.
 *      Consists of objects with two keys: column - column name from
 *      student list table (COLUMNS_NAMES const), order - sorting order('asc', 'desc').
 *      For every table column, we have its own sorting rules that are described in getSortingRules.
 *      Order of elements in sortingParams provides the order in which those sorting rules
 *      will be applied to students list
 * @param {string} period - time of year
 * @param {string} appGroup - selected application group (READING, ESPANOL ...)
 * @returns {array}
 */
export const sortStudents = (students, sortingParams, period, appGroup) => {
    const { sortingRules, orders } = sortingParams.reduce(
        (config, { column, order }) => {
            const columnSortingRules = getSortingRules(period)[appGroup][column];

            config.sortingRules.push(...columnSortingRules);
            config.orders.push(...columnSortingRules.map(() => order));

            return config;
        },
        {
            sortingRules: [],
            orders: [],
        },
    );

    return orderBy(students, sortingRules, orders);
};
